import React, { useState, useMemo } from 'react';
import { PlusCircleIcon, DocumentTextIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { deleteNote } from '../moduleServices/notepad_services';
import { useNotepadViewMode } from '../tab_settings/viewServices/useNotepadViewMode';
import NoteCard from './viewComponents/note_card';
import DrawerNoteForm from './viewComponents/drawer_form';
import SpinnerLoad from '../moduleComponents/spinner_load';

const MainNotes = ({ notes, isNotesLoading, refreshNotes, updateNotesOptimistically, clusters }) => {
  const { viewMode } = useNotepadViewMode();
  const [isModalOpen, setIsModalOpen] = useState(false);
  // ##### REFACTORED: Use selectedNoteId instead of editNote state
  // Single source of truth: selectedNote is derived from notes array
  const [selectedNoteId, setSelectedNoteId] = useState(null);

  // Derive selectedNote from notes array (single source of truth)
  const selectedNote = useMemo(() => {
    if (!selectedNoteId) return null;
    return notes.find(n => n.id === selectedNoteId) || null;
  }, [notes, selectedNoteId]);

  const handleNewNote = () => {
    setSelectedNoteId(null);
    setIsModalOpen(true);
  };

  const handleEditNote = (note) => {
    setSelectedNoteId(note.id);
    setIsModalOpen(true);
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await deleteNote(noteId);
      await refreshNotes();
      // If deleted note was selected, close drawer
      if (selectedNoteId === noteId) {
        setIsModalOpen(false);
        setSelectedNoteId(null);
      }
    } catch (error) {
      throw error;
    }
  };

  // ##### REFACTORED: Simplified - optimistic update already updates notes array
  // No need to manually sync editNote, it's derived from notes array
  const handleOptimisticUpdate = (updatedNoteData) => {
    if (updatedNoteData && updatedNoteData.id) {
      updateNotesOptimistically(updatedNoteData);
    }
  };

  // ##### REFACTORED: Simplified - just refresh if drawer is closed
  // When drawer is open, optimistic update already updated notes array
  const handleDrawerClose = () => {
    setIsModalOpen(false);
    setSelectedNoteId(null);
  };

  if (isNotesLoading && !isModalOpen) {
    return (
      <div className="p-2">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, idx) => (
            <div key={idx} className="bg-white rounded-xl shadow p-3 animate-pulse">
              <div className="h-5 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded mb-1"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-2 space-y-4">
        {/* Notes Grid */}
      {notes.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <DocumentTextIcon className="h-16 w-16 text-gray-300 mb-4" />
          <p className="text-gray-400">No notes yet. Create your first note!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {notes.map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              onClick={() => handleEditNote(note)}
              viewMode={viewMode}
            />
          ))}
        </div>
      )}

      </div>

      {/* FAB Button Add */}
      <button
        onClick={handleNewNote}
        className="fixed bottom-20 right-6 bg-yellow-500 hover:bg-yellow-600 text-white p-3 rounded-full shadow-lg"
      >
        <PlusCircleIcon className="h-8 w-8" />
      </button>

      {/* Drawer */}
      <DrawerNoteForm
        isOpen={isModalOpen}
        onClose={handleDrawerClose}
        onOptimisticUpdate={handleOptimisticUpdate}
        editNote={selectedNote}
        onDelete={handleDeleteNote}
        clusters={clusters}
      />
    </>
  );
};

export default MainNotes;
